// PreviewQuestionnaire.tsx
import React, { useRef, useEffect } from "react"

import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faEye } from "@fortawesome/free-solid-svg-icons"

import { Loader } from "shared/ui/Loader/Loader.component"

/**
 * Props interface for the PreviewQuestionnaire component
 */
interface PreviewQuestionnaireProps {
  /** Indicates if the preview is being generated */
  isConvertingToWordPreview: boolean
  /** The HTML content to be displayed in the preview */
  previewContent: string
  /** Function to control the visibility of the preview mode */
  setPreviewVisible: (visible: boolean) => void
}

/**
 * PreviewQuestionnaire component renders a preview of the questionnaire within an iframe.
 *
 * @component
 * @param props - The component props
 * @returns The rendered PreviewQuestionnaire component
 */
const PreviewQuestionnaire: React.FC<PreviewQuestionnaireProps> = ({
  isConvertingToWordPreview,
  previewContent,
  setPreviewVisible,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!isConvertingToWordPreview && iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument
      if (iframeDoc) {
        iframeDoc.open()
        iframeDoc.write(previewContent)
        iframeDoc.close()
      }
    }
  }, [isConvertingToWordPreview, previewContent])

  return (
    <div className="flex flex-col w-full h-full">
      {!isConvertingToWordPreview ? (
        <div className="flex flex-col h-full">
          <div className="flex flex-row w-full p-2.5 min-h-[48px] bg-white shadow-sm items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <FontAwesomeIcon
                icon={faEye}
                className="h-4"
                style={{ color: "#36C3ED" }}
              />
              <span className="font-medium">Preview</span>
            </div>
            <Tooltip title="Close Preview">
              <FontAwesomeIcon
                icon={faClose}
                className="h-4 cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => setPreviewVisible(false)}
              />
            </Tooltip>
          </div>
          <iframe
            ref={iframeRef}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            title="Preview"
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full">
          <Loader fontSize={48} color="#36C3ED" />
          Generating preview...
        </div>
      )}
    </div>
  )
}

export default PreviewQuestionnaire
